import type { AggregateConstraintValidation } from '@moment/horizon-data-access/proposal'
import type { ComplianceMap } from '@/portfolio/proposal-builder/components/risk-factor-list/types'
import type {
  SleeveCompliance,
  SleeveID,
} from '@/portfolio/proposal-builder/types'

import {
  ComplianceCheckType,
  type ModelCompliance,
} from '@/types/api/compliance'
import { isAggregateValidation } from '@/types/api/constraints'

// Per-constraint validations for one sleeve, keyed by constraint id.
export function getSleeveComplianceMap(
  sleeveCompliance: Record<SleeveID, SleeveCompliance> | undefined,
  sleeveId: SleeveID | undefined,
): ComplianceMap {
  const compliance = sleeveId ? sleeveCompliance?.[sleeveId] : undefined
  if (!compliance?.after) return {}
  return compliance.after
    .filter((c) => c.type === ComplianceCheckType.ModelCompliance)
    .reduce<ComplianceMap>((acc, c) => {
      const modelCompliance = c as ModelCompliance
      for (const [id, validation] of Object.entries(
        modelCompliance.constraints,
      )) {
        if (!isAggregateValidation(validation)) {
          acc[id] = validation
        }
      }
      return acc
    }, {})
}

// Aggregate (cross-constraint) validations for one sleeve.
export function getSleeveAggregateValidations(
  sleeveCompliance: Record<SleeveID, SleeveCompliance> | undefined,
  sleeveId: SleeveID | undefined,
): AggregateConstraintValidation[] {
  const compliance = sleeveId ? sleeveCompliance?.[sleeveId] : undefined
  if (!compliance?.after) return []
  const aggregates: AggregateConstraintValidation[] = []
  for (const c of compliance.after) {
    if (c.type !== ComplianceCheckType.ModelCompliance) continue
    const modelCompliance = c as ModelCompliance
    for (const validation of Object.values(modelCompliance.constraints)) {
      if (isAggregateValidation(validation)) {
        aggregates.push(validation)
      }
    }
  }
  return aggregates
}
